import { useConfig } from '@exam-center/configuration';
import {
  useGetUploadSignedUrlForImportExamineesAnswersLazyQuery,
  useImportExamineesAnswersSpreadSheetMutation,
  GetExamEventByIdDocument,
} from '@exam-center/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormFileInput,
  Link,
  Text,
  UploadFileIcon,
  type DialogProps,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { groupBy, zodResolver } from '@shared/utils';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { type ImportExamineeAnswersErrorDetails } from '../../types';
import { ImportExamineeAnswersErrorAlert } from '../import-examinee-answers-error-alert';

import {
  ALLOWED_FILE_TYPES,
  ImportExamineeAnswersSchema,
  MAX_FILE_SIZE_IN_MB,
  type ImportExamineeAnswersFormType,
} from './schema';

type ImportExamineeAnswersFormModalProps = Pick<DialogProps, 'open'> & {
  onClose: () => void;
  examEventId: string;
};

export default function ImportExamineeAnswersFormModal(
  props: Readonly<ImportExamineeAnswersFormModalProps>,
) {
  const { examEventId, open, onClose } = props;

  const configuration = useConfig();
  const importTemplateUrl = configuration.examEvent.paperAnswer.importTemplateUrl;

  const [errorMessages, setErrorMessages] = useState<Record<
    string,
    ImportExamineeAnswersErrorDetails[]
  > | null>(null);

  const formContext = useForm<ImportExamineeAnswersFormType>({
    resolver: zodResolver(ImportExamineeAnswersSchema),
  });

  const { showSnackbar } = useSnackbar();

  const [getUploadSignedUrl] = useGetUploadSignedUrlForImportExamineesAnswersLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [importExamineeAnswers] = useImportExamineesAnswersSpreadSheetMutation();

  const {
    formState: { isSubmitting },
    reset,
  } = formContext;

  const handleImportAnswers = async (data: ImportExamineeAnswersFormType) => {
    const { file } = data;

    const { data: getUploadSignedUrlResult } = await getUploadSignedUrl({
      variables: {
        getUploadSignedUrlForImportExamineesAnswersInput: {
          contentType: file.type,
          examEventId,
        },
      },
    });

    if (!getUploadSignedUrlResult?.ecGetUploadSignedUrlForImportExamineesAnswers.signedUrl) {
      throw new Error('Failed to get signed URL');
    }

    const { signedUrl, storageObject } =
      getUploadSignedUrlResult.ecGetUploadSignedUrlForImportExamineesAnswers;

    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok || !storageObject?.fileName) {
      throw new Error('Failed to upload file');
    }

    await importExamineeAnswers({
      variables: {
        importExamineesAnswersSpreadSheetInput: {
          examEventId,
          fileName: storageObject.fileName,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'นำเข้าไฟล์คำตอบเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: (error) => {
        // TODO: Handle error message from graphql to display error message
        const graphQLErrors = error.graphQLErrors[0].extensions
          ?.details as ImportExamineeAnswersErrorDetails[];
        setErrorMessages(groupBy(graphQLErrors, (error) => error.code));

        showSnackbar({
          message: 'นำเข้าไฟล์คำตอบไม่สำเร็จ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetExamEventByIdDocument],
    });
  };

  const handleClose = () => {
    setErrorMessages(null);
    onClose();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        slotProps={{
          paper: {
            component: Form,
            onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
              event.stopPropagation();
              await formContext.handleSubmit(handleImportAnswers)(event);
            },
          },
          transition: {
            onExited: () => {
              reset({});
            },
          },
        }}
        open={open}
        onClose={handleClose}
        maxWidth='sm'
      >
        <DialogTitle onClose={handleClose}>ตรวจข้อสอบ</DialogTitle>
        <DialogContent
          sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) })}
          dividers
        >
          {errorMessages ? (
            <ImportExamineeAnswersErrorAlert errors={errorMessages} />
          ) : (
            <Alert severity='warning'>
              <Text>
                การตรวจข้อสอบทำได้เพียง 1 ครั้งต่อรอบ/ชุด
                <br />
                โปรดตรวจสอบให้แน่ใจว่าข้อมูลผู้เข้าสอบครบถ้วนก่อนกด “Import”
                <br />
                หากนำเข้าข้อมูลสำเร็จแล้ว จะไม่สามารถอัปโหลดเพิ่มเติมหรือซ้ำได้อีก
              </Text>
            </Alert>
          )}
          <Text variant='body2Semibold'>
            คุณสามารถอัปโหลดข้อมูลได้สูงสุด 1 ไฟล์ (ขนาดไม่เกิน {MAX_FILE_SIZE_IN_MB} MB)
          </Text>
          <FormFileInput
            name='file'
            placeholder='กรุณาอัปโหลดไฟล์ .CSV หรือ .XLSX'
            accept={ALLOWED_FILE_TYPES}
            helperText={
              <Text color='text.secondary' variant='caption'>
                ตัวอย่างเอกสารสำหรับอัปโหลด{' '}
                <Link
                  color='info'
                  rel='noreferrer'
                  sx={{ cursor: 'pointer' }}
                  target='_blank'
                  href={importTemplateUrl}
                >
                  ดาวน์โหลดไฟล์ตัวอย่าง
                </Link>
              </Text>
            }
            label='แนบไฟล์'
            required
          />
          <Text variant='body2Semibold'>
            ระบบจะส่งอีเมลแจ้งผลการตรวจข้อสอบให้คุณโดยอัตโนมัติ หลังจากดำเนินการเสร็จสิ้น
          </Text>
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            disabled={isSubmitting}
            onClick={handleClose}
            color='secondary'
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            loadingPosition='start'
            disabled={isSubmitting}
            loading={isSubmitting}
            startIcon={<UploadFileIcon />}
            size='large'
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
